import { type EventListQuery } from "@playarena/shared";
import { Search, Filter, Grid, List } from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import EventCard from "../../../components/event/event-card/EventCard";
import EventFilter from "../../../components/event/event-filter/EventFilter";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Skeleton } from "../../../components/ui/skeleton";
import { useDebounce } from "../../../hooks/useDebounce";
import { useGetEventsQuery } from "../../../store/api/eventApi";

const EventList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [filters, setFilters] = useState<EventListQuery>({});
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data, isLoading, error } = useGetEventsQuery({
    ...filters,
    search: debouncedSearch,
    page,
    limit: 12,
  });

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value.toString());
      });
    }
    setSearchParams(params);
  }, [debouncedSearch, filters, setSearchParams]);

  const handleFilterChange = (newFilters: EventListQuery) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  return (
    <div className='container py-8'>
      <div className='mb-8'>
        <h1 className='mb-4 text-3xl font-bold'>Discover Events</h1>
        <p className='mb-6 text-muted-foreground'>
          Find and book tickets for exciting sports events
        </p>

        {/* Search and View Controls */}
        <div className='mb-6 flex flex-col gap-4 sm:flex-row'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground' />
            <Input
              placeholder='Search events by name, location, or sport...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='pl-10'
            />
          </div>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={() => setShowFilters(!showFilters)}
              className='flex items-center gap-2'
            >
              <Filter className='h-4 w-4' />
              Filters
            </Button>
            <div className='flex rounded-md border'>
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size='sm'
                onClick={() => setViewMode("grid")}
                className='rounded-r-none'
              >
                <Grid className='h-4 w-4' />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size='sm'
                onClick={() => setViewMode("list")}
                className='rounded-l-none'
              >
                <List className='h-4 w-4' />
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className='mb-6'>
            <CardContent className='pt-6'>
              <EventFilter filters={filters} onFiltersChange={handleFilterChange} />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results */}
      {error && (
        <div className='py-8 text-center'>
          <p className='text-destructive'>Failed to load events. Please try again.</p>
        </div>
      )}

      {isLoading && (
        <div
          className={`grid gap-6 ${viewMode === "grid" ? "md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"}`}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <Skeleton className='aspect-video' />
              <CardContent className='p-4'>
                <Skeleton className='mb-2 h-6' />
                <Skeleton className='mb-2 h-4' />
                <Skeleton className='h-4 w-2/3' />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data?.data && (
        <>
          <div className='mb-6'>
            <p className='text-sm text-muted-foreground'>
              Showing {data.data.length} of {data?.pagination?.totalItems} events
            </p>
          </div>

          <div
            className={`grid gap-6 ${viewMode === "grid" ? "md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"}`}
          >
            {data.data.map(event => (
              <EventCard key={event.id} event={event} viewMode={viewMode} />
            ))}
          </div>

          {data.pagination?.hasNextPage && (
            <div className='mt-8 text-center'>
              <Button onClick={handleLoadMore} variant='outline'>
                Load More Events
              </Button>
            </div>
          )}

          {data.data.length === 0 && (
            <div className='py-12 text-center'>
              <p className='mb-4 text-muted-foreground'>No events found matching your criteria.</p>
              <Button
                onClick={() => {
                  setSearchTerm("");
                  setFilters({});
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventList;
